import { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { usePortfolio } from '../../context/PortfolioContext';

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = [
  '#00d4ff', '#00e5a0', '#fb923c', '#ffc940', '#c084fc', 
  '#ff3d5a', '#60a5fa', '#a78bfa', '#34d399', '#f472b6'
];

function AllocationChart() {
  const { holdings } = usePortfolio();

  const chartData = useMemo(() => {
    if (!holdings?.length) return null;

    // Group by ticker, sum market values
    const byTicker = {};
    holdings.forEach(h => {
      const ticker = h.ticker?.toUpperCase();
      if (!ticker) return;
      if (!byTicker[ticker]) {
        byTicker[ticker] = { ticker, name: '', value: 0 };
      }
      if (!byTicker[ticker].name && h.name && h.name !== ticker) {
        byTicker[ticker].name = h.name;
      }
      byTicker[ticker].value += Number(h.market_value_gbp || h.mvGbp || 0);
    });

    const items = Object.values(byTicker)
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);

    if (items.length === 0) return null;

    const total = items.reduce((sum, item) => sum + item.value, 0);
    
    // If more than 10 items, group small ones as "Others"
    let chartItems = items;
    if (items.length > 10) {
      const topItems = items.slice(0, 9);
      const othersValue = items.slice(9).reduce((sum, item) => sum + item.value, 0);
      chartItems = [...topItems, { ticker: 'Others', name: '', value: othersValue }];
    }

    return {
      labels: chartItems.map(i => i.ticker),
      datasets: [{
        data: chartItems.map(i => i.value),
        backgroundColor: COLORS.slice(0, chartItems.length),
        borderColor: '#0f223a',
        borderWidth: 2,
        hoverOffset: 8,
      }],
      total,
      items: chartItems.map(i => ({
        ...i,
        percent: (i.value / total) * 100,
      })),
    };
  }, [holdings]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Custom legend below
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percent = ((value / total) * 100).toFixed(1);
            const formatted = new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(value);
            return `${context.label}: ${formatted} (${percent}%)`;
          },
        },
      },
    },
    cutout: '60%',
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  if (!chartData) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <span className="chart-title">Asset Allocation</span>
        </div>
        <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">No holdings data</span>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <span className="chart-title">Asset Allocation</span>
        <span className="data-table-badge">By Market Value</span>
      </div>
      
      <div className="donut-layout donut-layout--tall">
        <div className="donut-canvas">
          <Doughnut data={chartData} options={options} />
        </div>

        <div className="donut-legend">
          {chartData.items.map((item, idx) => (
            <div 
              key={item.ticker} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '0.5rem',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
                <span 
                  style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '50%', 
                    backgroundColor: COLORS[idx % COLORS.length],
                    flexShrink: 0,
                  }} 
                />
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{item.ticker}</span>
                  {item.name && (
                    <span
                      style={{
                        fontSize: '0.6875rem',
                        color: 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      title={item.name}
                    >
                      {item.name}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'Space Mono' }}>
                  {formatCurrency(item.value)}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  {item.percent.toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AllocationChart;
